import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar as MuiAvatar,
  Menu,
  MenuItem,
  Badge,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Dashboard,
  People,
  Schedule,
  EventAvailable,
  Payments,
  Work,
  RocketLaunch,
  TrendingUp,
  School,
  Business,
  BarChart,
  Settings,
  Menu as MenuIcon,
  Notifications,
  Logout,
  Person,
  ChevronLeft,
  Chat as ChatIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../store/slices/authSlice";
import { toggleSidebar, setSidebarOpen } from "../../store/slices/uiSlice";
import { ROLES_CONFIG, AVATAR_COLORS } from "../../types";
import Avatar from "../shared/Avatar";

const DRAWER_WIDTH = 240;

const NAV_META: Record<string, { icon: React.ReactNode; label: string }> = {
  dashboard: { icon: <Dashboard />, label: "Dashboard" },
  chat: { icon: <ChatIcon />, label: "Chat" },
  employees: { icon: <People />, label: "Employees" },
  attendance: { icon: <Schedule />, label: "Attendance" },
  leave: { icon: <EventAvailable />, label: "Leave" },
  payroll: { icon: <Payments />, label: "Payroll" },
  recruitment: { icon: <Work />, label: "Recruitment" },
  onboarding: { icon: <RocketLaunch />, label: "Onboarding" },
  performance: { icon: <TrendingUp />, label: "Performance" },
  training: { icon: <School />, label: "Training" },
  departments: { icon: <Business />, label: "Departments" },
  reports: { icon: <BarChart />, label: "Reports" },
  settings: { icon: <Settings />, label: "Settings" },
};

const Shell: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const employees = useAppSelector((s) => s.data.employees);
  const sidebarOpen = useAppSelector((s) => s.ui.sidebarOpen);
  const notifications = useAppSelector((s) => s.data.notifications);
  const leaveRequests = useAppSelector((s) => s.data.leaveRequests) || [];
  
  const roleConfig = user ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG] : null;
  const canApproveLeave = roleConfig?.canApproveLeave || false;
  const pendingLeavesCount = leaveRequests.filter((l: any) => l.status === "Pending").length;

  const dynamicNotifications = [];
  if (pendingLeavesCount > 0) {
    dynamicNotifications.push({
      id: `pending_leaves_${pendingLeavesCount}`,
      type: "leave",
      message: canApproveLeave 
        ? `You have ${pendingLeavesCount} pending leave request(s) to review.`
        : `You have ${pendingLeavesCount} leave request(s) pending approval.`,
      time: "Just now",
      read: false
    });
  }

  const allNotifications = [...dynamicNotifications, ...notifications];

  // Track which notification IDs have been "seen" by the user — persisted across refreshes
  const [seenIds, setSeenIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('hrms_seen_notif_ids');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const unseenCount = allNotifications.filter((n) => !seenIds.has(n.id)).length;

  const empProfile = employees.find((e: any) => e.user === user?._id || e.user?._id === user?._id);
  const displayName = empProfile?.name || user?.name || user?.username || 'User';
  const displayInitials = empProfile?.initials || displayName.charAt(0).toUpperCase();
  const displayColor = empProfile?.avColor || 'blue';
  const displayDept = empProfile?.dept || 'Administration';

  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);

  const handleOpenNotifs = (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(e.currentTarget);
    // Mark all currently visible notifications as seen and persist to localStorage
    const allIds = allNotifications.map((n) => n.id);
    const newSeenIds = new Set([...seenIds, ...allIds]);
    setSeenIds(newSeenIds);
    try {
      localStorage.setItem('hrms_seen_notif_ids', JSON.stringify([...newSeenIds]));
    } catch {}
  };

  useEffect(() => {
    if (isMobile) dispatch(setSidebarOpen(false));
    else dispatch(setSidebarOpen(true));
  }, [isMobile, dispatch]);

  const navItems = roleConfig
    ? roleConfig.nav.map((k) => ({ key: k, ...NAV_META[k] }))
    : [];

  const currentPage = location.pathname.split("/").pop() || "dashboard";
  const pageTitle = NAV_META[currentPage]?.label || "Dashboard";

  const handleNav = (key: string) => {
    navigate(`/app/${key}`);
    if (isMobile) dispatch(setSidebarOpen(false));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          px: 2,
          py: 2.2,
          borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            background: "linear-gradient(135deg,#85D1DB,#B6F2D1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            color: "#fff",
          }}
        >
          HR
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: -0.3,
              color: "#0f172a",
            }}
          >
            HRCore
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              color: "#85D1DB",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {roleConfig?.label}
          </Typography>
        </Box>
      </Box>

      {/* Nav */}
      <List
        sx={{
          flex: 1,
          py: 1.2,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "#e2e8f0",
            borderRadius: 99,
          },
        }}
      >
        {navItems.map((item) => {
          const active = currentPage === item.key;
          return (
            <Tooltip
              title={item.label}
              placement="right"
              key={item.key}
              disableHoverListener
            >
              <ListItem
                onClick={() => handleNav(item.key)}
                sx={{
                  py: 1,
                  px: 2,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  cursor: "pointer",
                  background: active ? "rgba(133,209,219,0.1)" : "transparent",
                  "&:hover": {
                    background: active
                      ? "rgba(133,209,219,0.15)"
                      : "rgba(241,245,249,0.8)",
                  },
                  transition: "all 0.15s",
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 36, color: active ? "#85D1DB" : "#94a3b8" }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      color: active ? "#6BB8C3" : "#64748b",
                    },
                  }}
                />
                {item.key === "leave" && pendingLeavesCount > 0 && (
                  <Box
                    sx={{
                      background: "#dc2626",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      px: 0.8,
                      borderRadius: 99,
                      lineHeight: 1.6,
                    }}
                  >
                    {pendingLeavesCount}
                  </Box>
                )}
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      {/* User footer */}
      <Box sx={{ borderTop: "1px solid #e5e7eb", p: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            p: 1.2,
            borderRadius: 2,
            background: "#f1f5f9",
            mb: 1,
            cursor: "pointer",
            "&:hover": { background: "#e2e8f0" },
          }}
          onClick={() => handleNav("myprofile")}
        >
          {user && (
            <Avatar initials={displayInitials} color={displayColor} size={32} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 500,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {displayDept}
            </Typography>
          </Box>
        </Box>
        <Box
          onClick={handleLogout}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.2,
            py: 0.8,
            borderRadius: 1.5,
            border: "1px solid #d1d5db",
            cursor: "pointer",
            fontSize: 12,
            color: "#64748b",
            transition: "all 0.15s",
            "&:hover": {
              background: "#fee2e2",
              borderColor: "#dc2626",
              color: "#dc2626",
            },
          }}
        >
          <Logout sx={{ fontSize: 16 }} /> Sign Out
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={() => dispatch(setSidebarOpen(false))}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            width: sidebarOpen ? DRAWER_WIDTH : 0,
            flexShrink: 0,
            transition: "width 0.2s",
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              border: "none",
              borderRight: "1px solid #e5e7eb",
              boxShadow: "none",
              transition: "width 0.2s",
              overflowX: "hidden",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Topbar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #e5e7eb",
            color: "#0f172a",
            top: 0,
            zIndex: 1100,
          }}
        >
          <Toolbar
            sx={{ px: { xs: 1.5, sm: 2 }, minHeight: "56px !important" }}
          >
            <IconButton
              onClick={() => dispatch(toggleSidebar())}
              sx={{ mr: 1, color: "#475569" }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              sx={{
                flex: 1,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: -0.3,
              }}
            >
              {pageTitle}
            </Typography>

            {/* Notifications */}
            <IconButton
              onClick={handleOpenNotifs}
              sx={{ color: "#64748b", mr: 0.5 }}
            >
              <Badge badgeContent={unseenCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notifAnchor}
              open={Boolean(notifAnchor)}
              onClose={() => setNotifAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    width: 340,
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    mt: 1,
                  },
                },
              }}
            >
              <Typography
                sx={{ px: 2, py: 1.5, fontSize: 13, fontWeight: 700 }}
              >
                Notifications
              </Typography>
              <Divider />
              {allNotifications.map((n) => (
                <MenuItem
                  key={n.id}
                  sx={{
                    px: 2,
                    py: 1.2,
                    alignItems: "flex-start",
                    borderLeft: n.read ? "none" : "2px solid #85D1DB",
                    background: n.read
                      ? "transparent"
                      : "rgba(133,209,219,0.03)",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: 12.5, color: "#0f172a", lineHeight: 1.4 }}
                    >
                      {n.message}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}
                    >
                      {n.time}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>

            {/* User avatar */}
            <IconButton
              onClick={(e) => setUserAnchor(e.currentTarget)}
              sx={{ p: 0.5 }}
            >
              {user && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: AVATAR_COLORS[displayColor] || AVATAR_COLORS.blue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  {displayInitials}
                </Box>
              )}
            </IconButton>
            <Menu
              anchorEl={userAnchor}
              open={Boolean(userAnchor)}
              onClose={() => setUserAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    mt: 1,
                  },
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {user && (
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: AVATAR_COLORS[displayColor] || AVATAR_COLORS.blue,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    {displayInitials}
                  </Box>
                )}
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {displayName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {roleConfig?.label}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleNav("myprofile");
                  setUserAnchor(null);
                }}
              >
                <Person sx={{ mr: 1.5, fontSize: 18 }} /> My Profile
              </MenuItem>
              {user?.role.toLowerCase() === "admin" && (
                <MenuItem
                  onClick={() => {
                    handleNav("settings");
                    setUserAnchor(null);
                  }}
                >
                  <Settings sx={{ mr: 1.5, fontSize: 18 }} /> Settings
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: "#dc2626" }}>
                <Logout sx={{ mr: 1.5, fontSize: 18 }} /> Sign Out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box
          sx={{
            flex: 1,
            background: "#f8fafc",
            p: { xs: 2, sm: 3 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Shell;
